import {
  Component,
  OnDestroy,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
} from '@angular/core';
import { AgGridAngular } from 'ag-grid-angular';
import { AsyncPipe } from '@angular/common';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
// Ag-Grid
import {
  GridOptions,
  GridApi,
  ColDef,
  CellContextMenuEvent,
  RowSelectionOptions,
} from 'ag-grid-community';
import { agGridDarkTheme } from 'src/app/shared/components/ag-grid/dark-theme';
// Services
import { SharedService } from '../../shared/services/shared.service';
import { AccountingService } from '../services/accounting.service';
import { ContextMenuService } from '../../shared/services/context-menu.service';

// Third Party
import { firstValueFrom, forkJoin, Subscription } from 'rxjs';
import * as Papa from 'papaparse';
import * as ExcelJS from 'exceljs';

// components
import { EditDeleteBtnsComponent } from 'src/app/shared/components/edit-delete-btns/edit-delete-btns.component';
import { CustomerTableColumnDef } from './columnDefs';
import {
  SalesReceipt,
  Customer,
  Vendor,
} from 'src/app/shared/models/accounting';
import {
  Answers,
  ComponentNames,
  ContactTypes,
  ModalID,
  ModalType,
  Questions,
  Statements,
  StatusModalIndicator,
} from 'src/app/shared/models/constants';
import {
  calculateFFLExpirationDate,
  isObjectEmpty,
  isValidFileType,
} from 'src/app/shared/functions';
import {
  getContactModelProperties,
  getAddressModelProperties,
  getFflModelProperties,
} from 'src/app/accounting/shared/functions';
import { AgGridService } from 'src/app/shared/services/ag-grid.service';

@Component({
  selector: 'app-contacts-table',
  templateUrl: './contacts-table.component.html',
  styleUrls: ['./contacts-table.component.scss'],
  standalone: true,
  imports: [AgGridAngular, AsyncPipe, BsDropdownModule],
})
export class ContactsTableComponent {
  @ViewChild('grid', { read: ElementRef }) gridElement!: ElementRef;
  @ViewChild('grid') grid!: AgGridAngular; // Keep for grid API access
  subscriptions = new Subscription();
  sales_records: SalesReceipt[] = JSON.parse(
    JSON.stringify(this.accountingService.sales_records),
  );
  contacts: any[] = [];

  // ag-grid properties Grid
  gridApi!: GridApi;

  columnDefs: ColDef[] = CustomerTableColumnDef;
  defaultColDef;
  gridOptions: GridOptions = {
    theme: agGridDarkTheme,
    suppressDragLeaveHidesColumns: true,
  };
  components: any;
  theme = agGridDarkTheme;

  // Context Menu state from service
  menuState$ = this.contextMenuService.menuState$;

  constructor(
    public sharedService: SharedService,
    public accountingService: AccountingService,
    private contextMenuService: ContextMenuService,
    private cdr: ChangeDetectorRef,
    private agGridService: AgGridService,
  ) {
    this.gridOptions = <GridOptions>{
      getRowStyle: (params) => {
        if (params.data.contact_type === ContactTypes.vendor) {
          return { color: 'lightskyblue' };
        } else if (params.data.contact_type === ContactTypes.customer) {
          return { color: 'coral' };
        } else {
          return null;
        }
      },
    };
    this.gridOptions.headerHeight = 50;
    this.gridOptions.rowHeight = 28;
    this.gridOptions.enableCellTextSelection = true; // Allows text selection for copying
    this.gridOptions.ensureDomOrder = true; // Ensures proper DOM order for accessibility
    this.gridOptions.suppressClipboardPaste = false; // Allow clipboard operations
    this.gridOptions.context = { ConfigComponent: this }; // this allows access to component functions
    this.gridOptions.rowClass = 'ag-grid-row';
    this.gridOptions.rowSelection = {
      mode: 'singleRow',
      enableSelectionWithoutKeys: false,
      checkboxes: false,
    } as RowSelectionOptions;
    this.defaultColDef = {
      sortable: true,
      filter: true,
      resizable: true,
      floatingFilter: true,
      wrapHeaderText: true,
    };
    this.components = { ViewEditBtns: EditDeleteBtnsComponent };
    // contact_to_add
    this.subscriptions.add(
      this.accountingService.customer_contact_to_add$.subscribe((response) => {
        this.addCustomer(response);
      }),
    );
    // customer_added
    this.subscriptions.add(
      this.accountingService.customer_list_updated$.subscribe((response) => {
        this.combineCustomersAndVendors();
      }),
    );
    // contact_to_edited
    this.subscriptions.add(
      this.accountingService.customer_contact_to_edit$.subscribe(
        (edit_customer_response_package) => {
          this.editCustomer(edit_customer_response_package.customer);
        },
      ),
    );
    // vendor_to_add
    this.subscriptions.add(
      this.accountingService.vendor_contact_to_add$.subscribe((response) => {
        this.addVendor(response);
      }),
    );
    // vendor_added
    this.subscriptions.add(
      this.accountingService.vendor_list_updated$.subscribe((response) => {
        this.combineCustomersAndVendors();
        this.accountingService.getVendorList();
      }),
    );
    // vendor_to_edited
    this.subscriptions.add(
      this.accountingService.vendor_contact_to_edit$.subscribe(
        (edit_vendor_response_package) => {
          this.editVendor(edit_vendor_response_package.vendor);
        },
      ),
    );
    // yes_no_submit$ - contact/vendor_to_delete
    this.subscriptions.add(
      this.sharedService.yes_no_submit$.subscribe((response) => {
        if (
          response['answer'] === Answers.yes &&
          response['question'] === Questions.delete_vendor
        ) {
          this.accountingService.deleteVendor(
            response['optional_details'] as Vendor,
          );
        } else if (
          response['answer'] === Answers.yes &&
          response['question'] === Questions.delete_customer
        ) {
          this.accountingService.deleteCustomer(
            response['optional_details'] as Customer,
          );
        } else {
          console.log('No action taken');
        }
      }),
    );
    // file has been selected from file picker
    this.subscriptions.add(
      this.sharedService.file_selected_from_picker$.subscribe((file_info) => {
        if (file_info.file) {
          if (isValidFileType(file_info.file, ['csv', 'xlsx'])) {
            if (file_info.file_format === ContactTypes.vendor) {
              this.verifyFile(file_info.file, ContactTypes.vendor);
            } else {
              this.verifyFile(file_info.file, ContactTypes.customer);
            }
          } else {
            this.sharedService.openMessageModal(
              Statements.incorrect_file_type_csv_xlsx,
            );
          }
        }
      }),
    );
    // file has been verified for import
    this.subscriptions.add(
      this.accountingService.file_verified$.subscribe(
        ({ file, contact_type }) => {
          console.log(contact_type);
          console.log(file);
          const file_type = file.name.split('.').pop()?.toLowerCase();
          if (contact_type === ContactTypes.vendor) {
            this.importVendorFile(file, file_type);
          } else if (contact_type === ContactTypes.customer) {
            this.importCustomerFile(file, file_type);
          }
        },
      ),
    );
  }
  async onGridReady(params: any) {
    this.gridApi = params.api;

    if (!this.agGridService.hasCachedSettingsFor(ComponentNames.contact_table)) {
      await this.agGridService.loadSavedTableSettings(
        ComponentNames.contact_table,
      );
    }
    this.applyCurrentSessionPreferences();
  }
  async ngOnInit() {
    this.addButtonColumn();
    this.combineCustomersAndVendors();
    if (
      this.accountingService.customers.length === 0 &&
      this.accountingService.vendors.length === 0
    ) {
      await this.refreshTableDataClick();
    }
  }
  ngAfterViewInit() {
    if (!this.gridElement) {
      console.error('Grid element not found');
      return;
    }
    this.contextMenuService.initialize(this.cdr, this.gridElement);
  }
  combineCustomersAndVendors() {
    const safeSort = (a, b) => {
      if (!a.id && !b.id) return 0;
      if (!a.id) return -1;
      if (!b.id) return 1;
      return a.id.localeCompare(b.id);
    };
    this.contacts = Array.from(
      new Set([
        ...this.accountingService.customers.sort(safeSort),
        ...this.accountingService.vendors.sort(safeSort),
      ]),
    );
    this.contacts = [...this.contacts];
  }
  async refreshTableDataClick() {
    this.sharedService.openStatusSpinner('Refreshing Contact Records');
    await firstValueFrom(
      forkJoin({
        vendors: this.accountingService.getVendors(),
        customers: this.accountingService.getCustomers(),
      }),
    ).then((response) => {
      this.combineCustomersAndVendors();
    });
    this.sharedService.closeStatusSpinner();
  }

  getCustomerName() {
    this.sales_records.forEach((record) => {
      const customer_name =
        this.accountingService.customers.find(
          (customer) => customer.contact_id === record.customer_id,
        ).contact.name_first +
        ' ' +
        this.accountingService.customers.find(
          (customer) => customer.contact_id === record.customer_id,
        ).contact.name_last;
      record.customer_name = customer_name;
    });

    this.sales_records = [...this.sales_records];
  }
  addButtonColumn() {
    const hasEditDelete = this.columnDefs.some(
      (column) => column.headerName === 'View / Edit',
    );
    if (!hasEditDelete) {
      this.columnDefs.push({
        // View / Edit
        headerName: 'View / Edit',
        field: 'view_edit',
        resizable: true,
        minWidth: 100,
        width: 100,
        pinned: 'right', // pin to right
        suppressMovable: true, // make not movable
        cellRenderer: 'ViewEditBtns',
        cellRendererParams: {
          editItem: this.editItemClick.bind(this),
          deleteItem: this.deleteItemClick.bind(this),
        },
      });
    }
  }
  editItemClick(selected_row: { colValues: any; rowIndex: number }) {
    const expense = selected_row.colValues;
    if (expense.contact_type === ContactTypes.customer) {
      this.sharedService.openAddEditContactModal(
        ModalType.edit,
        ContactTypes.customer,
        selected_row.colValues,
        selected_row.rowIndex,
      );
      setTimeout(() => {
        this.sharedService.view_edit_item$.next(expense);
      }, 125);
    } else {
      console.warn(selected_row.colValues);
      this.sharedService.openAddEditContactModal(
        ModalType.edit,
        ContactTypes.vendor,
        selected_row.colValues,
        selected_row.rowIndex,
      );
      setTimeout(() => {
        this.sharedService.view_edit_item$.next(expense);
      }, 125);
    }
  }
  deleteItemClick(selected_row: { colValues: any; rowIndex: number }) {
    if (selected_row.colValues['contact_type'] === ContactTypes.customer) {
      this.sharedService.openYesNoModal(
        Questions.delete_customer,
        selected_row.colValues,
      );
    } else {
      this.sharedService.openYesNoModal(
        Questions.delete_vendor,
        selected_row.colValues,
      );
    }
  }
  createNewCustomerContactClick() {
    this.sharedService.openAddEditContactModal(
      ModalType.add,
      ContactTypes.customer,
    );
  }
  createNewVendorContactClick() {
    this.sharedService.openAddEditContactModal(
      ModalType.add,
      ContactTypes.vendor,
    );
  }
  addVendor(vendor: Vendor) {
    console.log('Adding a new Vendor', vendor);
    this.accountingService.addNewVendor(vendor);
  }
  async addCustomer(customer: Customer) {
    this.sharedService.openStatusSpinner('Adding Customer');
    console.log('Adding a new Customer', customer);
    await this.accountingService.addNewCustomer(customer);
    this.sharedService.closeStatusSpinner();
  }
  editCustomer(customer: Customer) {
    this.accountingService.editCustomer(customer);
  }
  editVendor(vendor: Vendor) {
    this.accountingService.editVendor(vendor);
  }

  verifyFile(file: File, file_format: string) {
    const contact = getContactModelProperties();
    const address = getAddressModelProperties();
    if (file_format === ContactTypes.vendor) {
      const ffl = getFflModelProperties();
      const vendor: string[] = ['memo'];
      const vendorColumns = [...contact, ...address, ...ffl, ...vendor];
      if (isValidFileType(file, ['csv', 'xlsx'])) {
        if (file.name.split('.').pop()?.toLowerCase() === 'xlsx') {
          this.sharedService.validateXlsxColumns(
            file,
            vendorColumns,
            file_format,
          );
        } else {
          this.sharedService.validateCSVColumns(
            file,
            vendorColumns,
            file_format,
          );
        }
      } else {
        this.sharedService.openMessageModal(
          'Invalid file type. Please select a .csv or .xlsx file.',
        );
      }
    } else {
      if (isValidFileType(file, ['csv', 'xlsx'])) {
        const customer: string[] = ['memo'];
        const customerColumns = [...contact, ...address, ...customer];
        if (file.name.split('.').pop()?.toLowerCase() === 'xlsx') {
          this.sharedService.validateXlsxColumns(
            file,
            customerColumns,
            file_format,
          );
        } else {
          this.sharedService.validateCSVColumns(
            file,
            customerColumns,
            file_format,
          );
        }
      } else {
        this.sharedService.openMessageModal(
          'Invalid file type. Please select a .csv or .xlsx file.',
        );
      }
    }
  }
  // validateCSVColumns(file: File, expected_columns: string[], contact_type: string) {
  //   Papa.parse(file, {
  //     header: true,
  //     complete: (results) => {
  //       const actual_columns = results.meta.fields;
  //       const missingColumns = expected_columns.filter(
  //         (col) => !actual_columns.includes(col)
  //       );
  //       const extraColumns = actual_columns.filter(
  //         (col) => !expected_columns.includes(col)
  //       );

  //       if (missingColumns.length > 0) {
  //         this.sharedService.openMessageModal(
  //           `Missing columns: ${missingColumns.join(', ')}`
  //         );
  //       } else if (extraColumns.length > 0) {
  //         this.sharedService.openMessageModal(
  //           `Extra columns: ${extraColumns.join(', ')}`
  //         );
  //       } else {
  //         this.accountingService.file_verified$.next({ file, contact_type });
  //       }
  //     },
  //     error: (error) => {
  //       console.error('Error parsing CSV file:', error);
  //     },
  //   });
  // }
  // validateXlsxColumns(file: File, expected_columns: string[], contact_type: string) {
  //   const reader = new FileReader();
  //   reader.onload = (event) => {
  //     const data = new Uint8Array(event.target?.result as ArrayBuffer);
  //     const workbook = XLSX.read(data, { type: 'array' });
  //     const firstSheetName = workbook.SheetNames[0];
  //     const worksheet = workbook.Sheets[firstSheetName];
  //     const actualColumns = XLSX.utils.sheet_to_json(worksheet, { header: 1 })[0] as string[];
  //     const missingColumns = expected_columns.filter(col => !actualColumns.includes(col));
  //     const extraColumns = actualColumns.filter(col => !expected_columns.includes(col));
  //     if (missingColumns.length > 0) {
  //      this.sharedService.openMessageModal(`Missing columns: ${missingColumns.join(', ')}`);
  //     } else if (extraColumns.length > 0) {
  //       this.sharedService.openMessageModal(`Extra columns: ${extraColumns.join(', ')}`);
  //     } else {
  //       this.accountingService.file_verified$.next({ file, contact_type });

  //     }
  //   };
  //   reader.readAsArrayBuffer(file);
  // }
  downloadImportTemplate(file_format: string) {
    if (file_format === ContactTypes.vendor) {
      const contact: string[] = getContactModelProperties();
      const address: string[] = getAddressModelProperties();
      const ffl: string[] = getFflModelProperties();
      const vendor: string[] = ['memo'];
      const vendorColumnHeaders: string[] = [
        ...contact,
        ...address,
        ...ffl,
        ...vendor,
      ];
      const file_name = 'Vendor_Import_Template';
      this.accountingService.buildAndOpenExcelFile(
        vendorColumnHeaders,
        file_name,
      );
    } else {
      const contact: string[] = getContactModelProperties();
      const address: string[] = getAddressModelProperties();
      const vendor: string[] = ['memo'];
      const file_name = 'Customer_Import_Template';
      const customerColumnHeaders: string[] = [
        ...contact,
        ...address,
        ...vendor,
      ];
      this.accountingService.buildAndOpenExcelFile(
        customerColumnHeaders,
        file_name,
      );
    }
  }

  importVendorFile(file: File, file_type: string) {
    if (file_type === 'xlsx') {
      const reader = new FileReader();
      reader.onload = async (event) => {
        const buffer = event.target?.result as ArrayBuffer;
        const workbook = new ExcelJS.Workbook();
        await workbook.xlsx.load(buffer);
        const worksheet = workbook.worksheets[0];
        const headers: string[] = [];
        worksheet.getRow(1).eachCell((cell) => { headers.push(String(cell.value ?? '')); });
        const vendor_import_list: any[] = [];
        worksheet.eachRow((row, rowNumber) => {
          if (rowNumber === 1) return;
          const obj: any = {};
          row.eachCell((cell, colNumber) => { obj[headers[colNumber - 1]] = cell.value; });
          vendor_import_list.push(obj);
        });
        let vendors: Vendor[] = [];

        for (let i = 0; i < vendor_import_list.length; i++) {
          const vendor = vendor_import_list[i];
          let new_vendor: Vendor = new Vendor();
          if (i === 0) {
            new_vendor.contact_id = this.accountingService.createContactId(
              ContactTypes.vendor,
            );
          } else {
            new_vendor.contact_id =
              this.accountingService.createContactIdForImport(
                ContactTypes.vendor,
                vendors,
              );
          }

          new_vendor.contact_type = 'Vendor';
          new_vendor.contact.company_name = vendor['company_name']
            ? vendor['company_name']
            : null;
          new_vendor.contact.display_name = vendor['display_name']
            ? vendor['display_name']
            : null;
          new_vendor.contact.name_title = vendor['name_title']
            ? vendor['name_title']
            : null;
          new_vendor.contact.name_first = vendor['name_first']
            ? vendor['name_first']
            : null;
          new_vendor.contact.name_last = vendor['name_last']
            ? vendor['name_last']
            : null;
          new_vendor.contact.name_suffix = vendor['name_suffix']
            ? vendor['name_suffix']
            : null;
          new_vendor.contact.email = vendor['email'] ? vendor['email'] : null;
          new_vendor.contact.phone_number = vendor['phone_number']
            ? vendor['phone_number']
            : null;
          new_vendor.contact.fax_number = vendor['fax_number']
            ? vendor['fax_number']
            : null;
          new_vendor.contact.website = vendor['website']
            ? vendor['website']
            : null;
          new_vendor.contact.name_to_print_on_checks = vendor[
            'name_to_print_on_checks'
          ]
            ? vendor['name_to_print_on_checks']
            : null;
          new_vendor.contact.other = vendor['other'] ? vendor['other'] : null;

          new_vendor.address.street = vendor['street']
            ? vendor['street']
            : null;
          new_vendor.address.street2 = vendor['street2']
            ? vendor['street2']
            : null;
          new_vendor.address.city = vendor['city'] ? vendor['city'] : null;
          new_vendor.address.state = vendor['state'] ? vendor['state'] : null;
          new_vendor.address.zip_code = vendor['zip_code']
            ? vendor['zip_code']
            : null;
          new_vendor.address.country = vendor['country']
            ? vendor['country']
            : null;

          new_vendor.ach_payments.account_number = null;
          new_vendor.ach_payments.routing_number = null;

          new_vendor.ffl.ffl = vendor['ffl'] ? vendor['ffl'] : null;
          new_vendor.ffl.legal_name = vendor['legal_name']
            ? vendor['legal_name']
            : null;
          new_vendor.ffl.business_name = vendor['business_name']
            ? vendor['business_name']
            : null;
          new_vendor.ffl.phone_number = vendor['phone_number']
            ? vendor['phone_number']
            : null;
          new_vendor.ffl.email = vendor['email'] ? vendor['email'] : null;
          new_vendor.ffl.notes = vendor['notes'] ? vendor['notes'] : null;
          new_vendor.ffl.expiration_date = new_vendor.ffl.ffl
            ? calculateFFLExpirationDate(new_vendor.ffl.ffl)
            : null;

          new_vendor.memo = vendor['memo'] ? vendor['memo'] : null;
          new_vendor.attachments = [];

          vendors.push(new_vendor);
        }
        this.accountingService.addMultipleVendors(vendors);
      };
      reader.readAsArrayBuffer(file);
    } else if (file_type === 'csv') {
      Papa.parse(file, {
        header: true,
        complete: (results) => {
          const vendors = results.data;
          console.log(vendors);
          //this.accountingService.addVendors(vendors);
        },
        error: (error) => {
          console.error('Error parsing CSV file:', error);
        },
      });
    }
  }
  importCustomerFile(file: File, file_type: string) {
    // combine with importVendorFile
    if (file_type === 'xlsx') {
      const reader = new FileReader();
      reader.onload = async (event) => {
        const buffer = event.target?.result as ArrayBuffer;
        const workbook = new ExcelJS.Workbook();
        await workbook.xlsx.load(buffer);
        const worksheet = workbook.worksheets[0];
        const headers: string[] = [];
        worksheet.getRow(1).eachCell((cell) => { headers.push(String(cell.value ?? '')); });
        const customer_import_list: any[] = [];
        worksheet.eachRow((row, rowNumber) => {
          if (rowNumber === 1) return;
          const obj: any = {};
          row.eachCell((cell, colNumber) => { obj[headers[colNumber - 1]] = cell.value; });
          customer_import_list.push(obj);
        });
        let customers: Customer[] = [];

        for (let i = 0; i < customer_import_list.length; i++) {
          const customer = customer_import_list[i];
          let new_customer: Customer = new Customer();
          if (i === 0) {
            new_customer.contact_id = this.accountingService.createContactId(
              ContactTypes.customer,
            );
          } else {
            new_customer.contact_id =
              this.accountingService.createContactIdForImport(
                ContactTypes.customer,
                customers,
              );
          }

          new_customer.contact_type = 'Customer';
          new_customer.contact.company_name = customer['company_name']
            ? customer['company_name']
            : null;
          new_customer.contact.display_name = customer['display_name']
            ? customer['display_name']
            : null;
          new_customer.contact.name_title = customer['name_title']
            ? customer['name_title']
            : null;
          new_customer.contact.name_first = customer['name_first']
            ? customer['name_first']
            : null;
          new_customer.contact.name_last = customer['name_last']
            ? customer['name_last']
            : null;
          new_customer.contact.name_suffix = customer['name_suffix']
            ? customer['name_suffix']
            : null;
          new_customer.contact.email = customer['email']
            ? customer['email']
            : null;
          new_customer.contact.phone_number = customer['phone_number']
            ? customer['phone_number']
            : null;
          new_customer.contact.fax_number = customer['fax_number']
            ? customer['fax_number']
            : null;
          new_customer.contact.website = customer['website']
            ? customer['website']
            : null;
          new_customer.contact.name_to_print_on_checks = customer[
            'name_to_print_on_checks'
          ]
            ? customer['name_to_print_on_checks']
            : null;
          new_customer.contact.other = customer['other']
            ? customer['other']
            : null;

          new_customer.address.street = customer['street']
            ? customer['street']
            : null;
          new_customer.address.street2 = customer['street2']
            ? customer['street2']
            : null;
          new_customer.address.city = customer['city']
            ? customer['city']
            : null;
          new_customer.address.state = customer['state']
            ? customer['state']
            : null;
          new_customer.address.zip_code = customer['zip_code']
            ? customer['zip_code']
            : null;
          new_customer.address.country = customer['country']
            ? customer['country']
            : null;

          Object.assign(new_customer.tax_option,
            this.sharedService.company_info.accounting_options.sales_tax_options[1]);

          new_customer.memo = customer['memo'] ? customer['memo'] : null;
          new_customer.attachments = [];

          customers.push(new_customer);
        }
        this.accountingService.addMultipleCustomers(customers);
      };
      reader.readAsArrayBuffer(file);
    } else if (file_type === 'csv') {
      Papa.parse(file, {
        header: true,
        complete: (results) => {
          const customers = results.data;
          console.log(customers);
          //this.accountingService.addCustomers(customers);
        },
        error: (error) => {
          console.error('Error parsing CSV file:', error);
        },
      });
    }
  }

  // preference functions
  storeCurrentSessionFilters() {
    this.agGridService.storeCurrentSessionFilters(
      ComponentNames.contact_table,
      this.gridApi.getFilterModel(),
    );
  }
  storeCurrentSessionColumnConfig() {
    this.agGridService.storeCurrentSessionColumnConfig(
      ComponentNames.contact_table,
      this.gridApi?.getColumnState(),
    );
  }
  applyCurrentSessionPreferences() {
    let component: any = this.agGridService.current_session_filtering.find(
      (component) => component.component_name === ComponentNames.contact_table,
    );
    if (component && !isObjectEmpty(component.filters)) {
      this.gridApi.setFilterModel(component.filters);
    }
    component = this.agGridService.current_session_column_config.find(
      (component) => component.component_name === ComponentNames.contact_table,
    );
    if (component && !isObjectEmpty(component.column_config)) {
      this.gridApi?.applyColumnState({
        state: component.column_config,
        applyOrder: true,
      });
    }
  }
  saveCurrentTablePreferencesClick() {
    const filters = this.gridApi.getFilterModel();
    const column_state = this.gridApi?.getColumnState();
    this.agGridService.saveCurrentTableSettings(
      ComponentNames.contact_table,
      filters,
      column_state,
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  // right click copy functions
  onCellContextMenu(event: CellContextMenuEvent) {
    this.contextMenuService.onCellContextMenu(event);
  }
  onMenuMouseEnter() {
    this.contextMenuService.onMenuMouseEnter();
  }
  onMenuMouseLeave() {
    this.contextMenuService.onMenuMouseLeave();
  }
  copyCellValue() {
    this.contextMenuService.copyCellValue();
  }
}

